import { useCallback, useEffect, useRef, useState } from "react";
import { userRequest } from "../api/requestMethods";
import useVendorAvailabilityUpdates from "./useVendorAvailabilityUpdates";
import { normalizeDeliverySlots } from "../utils/deliverySchedule";

const LOAD_ERROR =
  "We could not load delivery times. Check your connection and try again.";

/**
 * Statuses this hook settles on:
 *   ready       windows loaded
 *   unavailable no vendor to ask about yet
 *   unsupported this deployment does not serve /api/delivery-slots
 *   error       the endpoint exists but the call failed, so a retry can work
 *
 * `unsupported` exists because the two are not the same thing to a customer.
 * A server that has not shipped the route yet is not a fault they can retry
 * away, and showing them a red-orange "we could not load delivery times, check
 * your connection" alert on a checkout that works perfectly well is a lie that
 * costs orders. Scheduling is simply not offered there; Express carries on.
 */
const endpointMissing = (error) => {
  const status = error?.response?.status;
  // 404 also covers "no such vendor", which offers no windows either way.
  return status === 404 || status === 405 || status === 501;
};

const EMPTY = {
  timezone: null,
  days: [],
  hasWindows: false,
  openNow: null,
  acceptsScheduledOrders: false,
  expressAvailable: true,
  schedulingAvailable: true,
  message: null,
};

/**
 * Delivery windows the customer can pick, from GET /api/delivery-slots.
 *
 * The server builds them from the seller's published hours in the market
 * timezone, and order create validates against the same resolver, so this hook
 * never computes a window itself. Uses the shared `userRequest` client, which
 * adds the single lowercase x-country header.
 *
 * @param {{vendorType:string, vendorId?:string, outletId?:string}|null} query
 *        null when the checkout cannot be scheduled yet.
 */
const useDeliverySlots = (query) => {
  const vendorType = query?.vendorType || null;
  const vendorId = query?.vendorId || null;
  const outletId = query?.outletId || null;

  const [state, setState] = useState(() => ({
    ...EMPTY,
    status: vendorType ? "loading" : "unavailable",
    error: null,
  }));
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!vendorType) {
      setState({ ...EMPTY, status: "unavailable", error: null });
      return null;
    }
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const response = await userRequest.get("/api/delivery-slots", {
        params: {
          vendorType,
          ...(vendorId ? { vendorId } : {}),
          ...(outletId ? { outletId } : {}),
          // Cache-buster. No Cache-Control request header: it is non-simple
          // and would force a CORS preflight.
          checkedAt: Date.now(),
        },
      });
      const slots = normalizeDeliverySlots(response?.data);
      if (!slots) {
        // A 200 that is not a slots body means the route is not mounted and
        // the SPA catch-all answered with index.html. Same meaning as a 404.
        if (requestId === requestIdRef.current) {
          setState({ ...EMPTY, status: "unsupported", error: null });
        }
        return null;
      }
      if (requestId === requestIdRef.current) {
        setState({ ...slots, status: "ready", error: null });
      }
      return slots;
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setState(
          endpointMissing(error)
            ? { ...EMPTY, status: "unsupported", error: null }
            : (previous) => ({
                ...previous,
                status: "error",
                error: error?.response?.data?.message || LOAD_ERROR,
              })
        );
      }
      return null;
    }
  }, [vendorType, vendorId, outletId]);

  useEffect(() => {
    setState({ ...EMPTY, status: vendorType ? "loading" : "unavailable", error: null });
    refresh();
    return () => {
      // Ignore a response that lands after unmount or a query change.
      requestIdRef.current += 1;
    };
  }, [refresh, vendorType]);

  useVendorAvailabilityUpdates(query, refresh);
  useEffect(() => {
    if (!vendorType) return undefined;
    const recheck = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [vendorType, refresh]);

  return { ...state, refresh };
};

export default useDeliverySlots;
